if (!customElements.get('sticky-cart-bar')) {
  customElements.define(
    'sticky-cart-bar',
    class StickyCartBar extends HTMLElement {
      constructor() {
        super();

        this.productId = this.dataset.productId;
        this.showWhen = this.dataset.showWhen;

        this.submitButton = this.querySelector('[data-sticky-cart-submit]');
        this.labelText = this.querySelector('[data-sticky-cart-label]');
        this.spinner = this.querySelector('.loading__spinner');
        this.priceTarget = this.querySelector('[data-sticky-cart-price]');
        this.infoButton = this.querySelector('.sticky-cart-bar__title-button');
        this.imageButton = this.querySelector('.sticky-cart-bar__image-button');

        // Cache the server-rendered label (respects the merchant's custom "Button label"
        // setting, or the localized "Add to cart" default) so we can restore it after
        // temporarily showing "Sold out" / "Unavailable".
        this.defaultLabel = this.labelText ? this.labelText.textContent.trim() : '';

        // This section never renders its own <form>. It reuses the real product form that
        // main-product.liquid / buy-buttons.liquid already renders on the page.
        const productInfo =
          (this.productId &&
            document.querySelector(`product-info[data-product-id="${this.productId}"]`)) ||
          document.querySelector('product-info');

        this.realForm = productInfo ? productInfo.querySelector('product-form form') : null;
        this.realSubmitButton = productInfo
          ? productInfo.querySelector('product-form [type="submit"]')
          : null;
        this.realPrice = productInfo ? productInfo.querySelector('[id^="price-"]') : null;
        this.productInfo = productInfo;
      }

      connectedCallback() {
        // If the real product form/button can't be found, stay inert rather than risk
        // submitting the wrong thing or showing state that can never update.
        if (!this.realForm || !this.realSubmitButton || !this.submitButton) return;

        this.sync();

        // Mirror the real submit button's disabled/loading state and the real price block's
        // content. A MutationObserver catches every path Dawn's own code uses to update them
        // (a normal variant change, an unavailable-combination state, an in-flight add-to-cart
        // request) without this section having to duplicate any of that logic itself.
        this.mutationObserver = new MutationObserver(() => this.sync());
        this.mutationObserver.observe(this.realSubmitButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
        });
        if (this.realPrice) {
          this.mutationObserver.observe(this.realPrice, {
            childList: true,
            subtree: true,
            characterData: true,
          });
        }

        this.submitButton.addEventListener('click', this.handleSubmitClick.bind(this));
        this.infoButton?.addEventListener('click', this.scrollToProduct.bind(this));
        this.imageButton?.addEventListener('click', this.scrollToProduct.bind(this));

        if (this.showWhen === 'always') {
          this.classList.add('is-visible');
        } else {
          this.setupVisibilityObserver();
        }
      }

      disconnectedCallback() {
        this.mutationObserver?.disconnect();
        this.visibilityObserver?.disconnect();
      }

      setupVisibilityObserver() {
        if (!('IntersectionObserver' in window)) {
          // No IntersectionObserver support: fail open rather than never showing the bar.
          this.classList.add('is-visible');
          return;
        }

        this.visibilityObserver = new IntersectionObserver(
          (entries) => {
            const primaryAddToCartVisible = entries[0]?.isIntersecting;
            this.classList.toggle('is-visible', !primaryAddToCartVisible);
          },
          { threshold: 0 }
        );
        this.visibilityObserver.observe(this.realSubmitButton);
      }

      sync() {
        const isSoldOut = this.realSubmitButton.hasAttribute('disabled');
        const isLoading = !isSoldOut && this.realSubmitButton.getAttribute('aria-disabled') === 'true';

        this.submitButton.disabled = isSoldOut;
        this.submitButton.classList.toggle('loading', isLoading);
        if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);

        if (this.labelText) {
          if (isSoldOut) {
            // Mirror the real button's own text so "Sold out" vs "Unavailable" (Dawn
            // distinguishes these) stays correct without this section re-deriving it.
            const realLabel = this.realSubmitButton.querySelector('span')?.textContent?.trim();
            this.labelText.textContent = realLabel || this.defaultLabel;
          } else {
            this.labelText.textContent = this.defaultLabel;
          }
        }

        if (this.priceTarget && this.realPrice) {
          this.priceTarget.innerHTML = this.realPrice.innerHTML;
        }
      }

      handleSubmitClick(event) {
        event.preventDefault();
        if (this.submitButton.disabled) return;
        if (this.realSubmitButton.getAttribute('aria-disabled') === 'true') return;

        // Forward the click to Dawn's real, unmodified product form. Its own submit handler
        // (assets/product-form.js) reads the form's live FormData - the currently selected
        // variant id, quantity, and any other field Dawn (or an app) has associated with it -
        // so this can never add a stale or wrong variant.
        if (typeof this.realForm.requestSubmit === 'function') {
          this.realForm.requestSubmit();
        } else {
          this.realForm.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      }

      scrollToProduct() {
        this.productInfo?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  );
}
